/** @format */

import React, { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuthStore } from "../../context/useAuthStore";

interface AllCollegeDataModalProps {
  onClose: () => void;
}

type CollegeData = {
  _id: string;
  collegeName: string;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const AllCollegeDataModal: React.FC<AllCollegeDataModalProps> = ({
  onClose,
}) => {
  const { token } = useAuthStore();
  const [colleges, setColleges] = useState<CollegeData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCollegeData = async () => {
      if (!token) {
        toast.error("Authentication token not found.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/college`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        if (isMounted) {
          if (res.data?.success && Array.isArray(res.data.success)) {
            const sortedColleges = res.data.success.sort(
              (a: CollegeData, b: CollegeData) =>
                a.collegeName.localeCompare(b.collegeName)
            );
            setColleges(sortedColleges);
          } else {
            setColleges([]);
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toast.error("Failed to fetch college data.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCollegeData();

    return () => {
      isMounted = false;
    };
  }, [token]);

  if (loading) {
    return (
      <section
        className="modalSection"
        role="dialog"
        aria-modal="true"
        aria-labelledby="college-list-modal"
      >
        <div className="modalContainer">
          <div className="flex justify-center items-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">
                Loading Colleges data...
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="modalSection"
      role="dialog"
      aria-modal="true"
      aria-labelledby="college-list-modal"
    >
      <div className="modalContainer">
        {/* Modal Header */}
        <div className="flex justify-between items-center gap-2 border-b pb-1 border-gray-200">
          <h2 id="college-list-modal" className="text-base font-semibold">
            All Colleges - ({colleges.length})
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="modalCloseButton"
              aria-label="Close modal"
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>

        {/* College Info */}
        <div className="logoutModalBody text-left">
          {colleges.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No colleges found.</p>
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-gray-600">
                <p>Total Colleges: {colleges.length}</p>
              </div>
              <div className="overflow-x-auto border rounded-md max-h-[calc(100vh-20rem)] overflow-y-auto">
                <table className="min-w-full border-collapse">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="tableHeadRow">
                      <th className="tableHeadValue">Sl.No</th>
                      <th className="tableHeadValue">College Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colleges.map((college, index) => (
                      <tr className="tableBodyRow" key={college._id || index}>
                        <td className="tableBodyValue text-sm">{index + 1}</td>
                        <td className="tableBodyValue text-sm">
                          {college.collegeName}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default AllCollegeDataModal;
